"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCommentStore } from "@/stores/commentStore";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { CommentFilters, CommentWithMovie } from "@/types/comment";

import { RefreshCw, MessageSquare, Filter } from "lucide-react";

export default function CommentPage() {
  const {
    comments,
    pagination,
    isLoading,
    isApproving,
    isRejecting,
    isDeleting,
    error,
    currentFilters,
    fetchComments,
    approveComment,
    rejectComment,
    deleteComment,
    setFilters,
    setCurrentPage,
  } = useCommentStore();

  const [selectedComment, setSelectedComment] =
    useState<CommentWithMovie | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

  // Load comments on mount
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Handle filter change
  const handleFilterChange = (status: string) => {
    const filters: CommentFilters = {
      ...currentFilters,
      status:
        status === "all" ? undefined : (status as CommentFilters["status"]),
      page: 1,
    };
    setFilters(filters);
    fetchComments(filters);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const filters: CommentFilters = {
      ...currentFilters,
      page,
    };
    setFilters(filters);
    fetchComments(filters);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchComments(currentFilters);
  };

  // Handle approve
  const handleApprove = async (id: string) => {
    try {
      await approveComment(id);
    } catch (error) {
      // Error is handled in store
    }
  };

  // Handle reject
  const handleReject = async (id: string) => {
    try {
      await rejectComment(id);
    } catch (error) {
      // Error is handled in store
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      await deleteComment(id);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      // Error is handled in store
    }
  };

  // Get status badge variant
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
            Chờ duyệt
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Đã duyệt
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive" className="bg-red-100 text-red-800">
            Đã từ chối
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading && comments.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-500 dark:text-gray-400">
              Đang tải danh sách bình luận...
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error && comments.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium mb-2">
              Lỗi khi tải danh sách bình luận
            </div>
            <div className="text-gray-500 dark:text-gray-400 mb-4">{error}</div>
            <Button onClick={() => fetchComments()}>Thử lại</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 flex items-center gap-2">
              <MessageSquare className="w-6 h-6" />
              Quản lý Bình luận
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <p className="text-gray-500 dark:text-gray-400">
                Duyệt và quản lý bình luận từ người dùng
              </p>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                {pagination.total} bình luận
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => fetchComments()}
              variant="outline"
              disabled={isLoading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Làm mới
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Trạng thái:
              </label>
              <Select
                value={currentFilters.status || "all"}
                onValueChange={handleFilterChange}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Chọn trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả</SelectItem>
                  <SelectItem value="pending">Chờ duyệt</SelectItem>
                  <SelectItem value="approved">Đã duyệt</SelectItem>
                  <SelectItem value="rejected">Đã từ chối</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-gray-500 dark:text-gray-400">
              Tổng: {pagination.total} bình luận
            </div>
          </div>
        </div>

        {/* Comments Table */}
        <div className="shadow-lg rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Đang tải...
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="text-red-600 text-lg font-medium mb-2">
                Lỗi khi tải dữ liệu
              </div>
              <div className="text-gray-500 dark:text-gray-400">{error}</div>
              <Button
                onClick={handleRefresh}
                className="mt-4 hover:cursor-pointer"
              >
                Thử lại
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Bình luận
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Phim
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Tác giả
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Ngày tạo
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {comments.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-gray-600 font-medium">
                              Chưa có bình luận
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                              {currentFilters.status
                                ? `Không có bình luận với trạng thái "${currentFilters.status}"`
                                : "Chưa có bình luận nào được gửi"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      comments.map((comment) => (
                        <tr
                          key={comment._id}
                          className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                        >
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-sm text-gray-900 line-clamp-2">
                                {comment.content}
                              </p>
                              <div className="text-xs text-gray-400 mt-1">
                                IP: {comment.userIp}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {comment.movieDetails?.name ||
                                `Movie ${comment.movie.slice(-6)}`}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {comment.authorName}
                              </div>
                              {comment.authorEmail && (
                                <div className="text-xs text-gray-500">
                                  {comment.authorEmail}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(comment.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(comment.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="hover:cursor-pointer"
                                onClick={() => {
                                  setSelectedComment(comment);
                                  setViewDialogOpen(true);
                                }}
                              >
                                Xem
                              </Button>
                              {comment.status === "pending" && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleApprove(comment._id)}
                                    disabled={isApproving}
                                    className="text-green-600 hover:text-green-700 hover:cursor-pointer"
                                  >
                                    Duyệt
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleReject(comment._id)}
                                    disabled={isRejecting}
                                    className="text-red-600 hover:text-red-700 hover:cursor-pointer"
                                  >
                                    Từ chối
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setCommentToDelete(comment._id);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={isDeleting}
                                className="text-red-600 hover:text-red-700 hover:cursor-pointer"
                              >
                                Xóa
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <Button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      variant="outline"
                      size="sm"
                      className="hover:cursor-pointer"
                    >
                      Trước
                    </Button>
                    <Button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      variant="outline"
                      size="sm"
                      className="hover:cursor-pointer"
                    >
                      Sau
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Hiển thị{" "}
                        <span className="font-medium">
                          {(pagination.page - 1) * 10 + 1}
                        </span>{" "}
                        đến{" "}
                        <span className="font-medium">
                          {Math.min(pagination.page * 10, pagination.total)}
                        </span>{" "}
                        trong{" "}
                        <span className="font-medium">{pagination.total}</span>{" "}
                        kết quả
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                          variant="outline"
                          size="sm"
                          className="rounded-l-md hover:cursor-pointer"
                        >
                          Trước
                        </Button>
                        {Array.from(
                          { length: pagination.totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <Button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            variant={
                              page === pagination.page ? "default" : "outline"
                            }
                            size="sm"
                            className="rounded-none hover:cursor-pointer"
                          >
                            {page}
                          </Button>
                        ))}
                        <Button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === pagination.totalPages}
                          variant="outline"
                          size="sm"
                          className="rounded-r-md hover:cursor-pointer"
                        >
                          Sau
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* View Comment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Chi tiết bình luận</DialogTitle>
          </DialogHeader>

          {selectedComment && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedComment.status)}
                <span className="text-sm text-gray-500">
                  {formatDate(selectedComment.createdAt)}
                </span>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Thông tin phim:
                </h4>
                <p className="text-sm text-gray-600">
                  {selectedComment.movieDetails?.name ||
                    `Movie ${selectedComment.movie.slice(-6)}`}
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Người bình luận:
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <strong>Tên:</strong> {selectedComment.authorName}
                  </p>
                  {selectedComment.authorEmail && (
                    <p>
                      <strong>Email:</strong> {selectedComment.authorEmail}
                    </p>
                  )}
                  <p>
                    <strong>IP:</strong> {selectedComment.userIp}
                  </p>
                  <p>
                    <strong>User Agent:</strong> {selectedComment.userAgent}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Nội dung:</h4>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <p className="text-gray-800">{selectedComment.content}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Đóng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Xác nhận xóa</DialogTitle>
            <DialogDescription>
              Bạn có chắc chắn muốn xóa bình luận này? Hành động này không thể
              hoàn tác.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setCommentToDelete(null);
              }}
            >
              Hủy
            </Button>
            <Button
              variant="destructive"
              onClick={() => commentToDelete && handleDelete(commentToDelete)}
            >
              Xóa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
